import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { calculateWeightProgress } from "../lib/weightUtils";
import { clientsAPI, progressAPI, workoutsAPI, photosAPI, notesAPI, plansAPI, clientAuthAPI, clientPaymentsAPI, dietPlansAPI, attendanceAPI } from "../lib/api";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { 
  ArrowLeft, Scale, TrendingUp, Dumbbell, Camera, FileText,
  Plus, Trash2, Calendar, Target, User, Phone, Mail, MessageCircle, Share2,
  Utensils, ClipboardList, AlertTriangle, Link2, Eye, EyeOff, History, Copy, IndianRupee, Download, UtensilsCrossed, CalendarCheck, Check
} from "lucide-react";
import ProgressCard from "../components/ProgressCard";
import ImageCropper from "../components/ImageCropper";
import WeeklyPlanEditor from "../components/WeeklyPlanEditor";
import { getImageUrl, getInitials } from "../lib/avatarUtils";
import { Avatar } from "../components/Avatar";

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { trainer } = useAuth();
  const fileInputRef = useRef(null);
  
  const [client, setClient] = useState(null);
  const [progress, setProgress] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [clientPayments, setClientPayments] = useState([]);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: "", mode: "cash", note: "", date: new Date().toISOString().split('T')[0] });
  const [dietPlans, setDietPlans] = useState([]);
  const [showAddDietPlan, setShowAddDietPlan] = useState(false);
  const [editingDietPlan, setEditingDietPlan] = useState(null);
  const [dietForm, setDietForm] = useState({
    name: "Diet Plan", notes: "",
    total_calories: "", total_protein: "", total_carbs: "", total_fat: "",
    meals: { breakfast: [], lunch: [], snack: [], dinner: [] }
  });
  const [mealInput, setMealInput] = useState({ name: "", calories: "", protein: "", carbs: "", fat: "", quantity: "" });
  const [addingMealTo, setAddingMealTo] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  
  // Form states
  const [showAddProgress, setShowAddProgress] = useState(false);
  const [showAddWorkout, setShowAddWorkout] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newProgress, setNewProgress] = useState({
    date: new Date().toISOString().split('T')[0],
    weight: "",
    body_fat_percent: "",
    chest: "",
    waist: "",
    hips: "",
    arms: "",
    thighs: "",
    calf: "",
    neck: "",
    notes: ""
  });
  
  const [newWorkout, setNewWorkout] = useState({
    date: new Date().toISOString().split('T')[0],
    exercise_name: "",
    sets: "",
    reps: "",
    weight: "",
    notes: ""
  });
  
  const [newNote, setNewNote] = useState({
    content: "",
    category: "general",
    visibility: "private"
  });

  // Weekly Plans state
  const [activePlan, setActivePlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlanDuration, setNewPlanDuration] = useState(4);
  const [newPlanTitle, setNewPlanTitle] = useState("Training Plan");
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [showPlanHistory, setShowPlanHistory] = useState(false);

  // Magic Link state
  const [magicLink, setMagicLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  // Profile photo upload state
  const profilePhotoInputRef = useRef(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  
  // Progress Card state
  const [showProgressCard, setShowProgressCard] = useState(false);
  const [loadingProgressCard, setLoadingProgressCard] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null, name: null });

  // Image crop state
  const [cropImage, setCropImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  // Goal change state
  const [showGoalChange, setShowGoalChange] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [createNewPlanOnGoalChange, setCreateNewPlanOnGoalChange] = useState(false);
  const [goalHistory, setGoalHistory] = useState([]);

  // Client edit state
  const [showEditClient, setShowEditClient] = useState(false);
  const [editData, setEditData] = useState({});

  // Coach notes state
  const [coachNotes, setCoachNotes] = useState("");
  const [savingCoachNotes, setSavingCoachNotes] = useState(false);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadAllData = async () => {
    try {
      const [clientRes, progressRes, workoutsRes, photosRes, notesRes] = await Promise.all([
        clientsAPI.getOne(clientId),
        progressAPI.getAll(clientId),
        workoutsAPI.getAll(clientId),
        photosAPI.getAll(clientId),
        notesAPI.getAll(clientId)
      ]);
      setClient(clientRes.data);
      setCoachNotes(clientRes.data.coach_notes || "");
      setProgress(progressRes.data);
      setWorkouts(workoutsRes.data);
      setPhotos(photosRes.data);
      setNotes(notesRes.data);

      // Load plans separately (non-blocking)
      try {
        const [activePlanRes, allPlansRes, goalHistoryRes, paymentsRes, dietPlansRes, attendanceRes, attendanceStatsRes] = await Promise.all([
          plansAPI.getActive(clientId),
          plansAPI.getAll(clientId),
          clientsAPI.getGoalHistory(clientId),
          clientPaymentsAPI.getAll(clientId),
          dietPlansAPI.getAll(clientId),
          attendanceAPI.getAll(clientId),
          attendanceAPI.getStats(clientId).catch(() => ({ data: null })),
        ]);
        setActivePlan(activePlanRes.data);
        setAllPlans(allPlansRes.data || []);
        setGoalHistory(goalHistoryRes.data || []);
        setClientPayments(paymentsRes.data || []);
        setDietPlans(dietPlansRes.data || []);
        setAttendance(attendanceRes.data || []);
        setAttendanceStats(attendanceStatsRes.data);
      } catch {
        // Plans/history/payments may not exist yet
      }
    } catch (error) {
      toast.error("Failed to load client data");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgress = async (e) => {
    e.preventDefault();
    if (!newProgress.weight) {
      toast.error("Please enter weight");
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        date: newProgress.date,
        weight: parseFloat(newProgress.weight),
        body_fat_percent: newProgress.body_fat_percent ? parseFloat(newProgress.body_fat_percent) : null,
        chest: newProgress.chest ? parseFloat(newProgress.chest) : null,
        waist: newProgress.waist ? parseFloat(newProgress.waist) : null,
        hips: newProgress.hips ? parseFloat(newProgress.hips) : null,
        arms: newProgress.arms ? parseFloat(newProgress.arms) : null,
        thighs: newProgress.thighs ? parseFloat(newProgress.thighs) : null,
        calf: newProgress.calf ? parseFloat(newProgress.calf) : null,
        neck: newProgress.neck ? parseFloat(newProgress.neck) : null,
        notes: newProgress.notes || null
      };
      await progressAPI.create(clientId, data);
      toast.success("Progress added!");
      setShowAddProgress(false);
      setNewProgress({
        date: new Date().toISOString().split('T')[0],
        weight: "", body_fat_percent: "", chest: "", waist: "", hips: "", arms: "", thighs: "", calf: "", neck: "", notes: ""
      });
      loadAllData();
    } catch (error) {
      toast.error("Failed to add progress");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWorkout = async (e) => {
    e.preventDefault();
    if (!newWorkout.exercise_name || !newWorkout.sets || !newWorkout.reps) {
      toast.error("Please fill in exercise, sets, and reps");
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        date: newWorkout.date,
        exercise_name: newWorkout.exercise_name,
        sets: parseInt(newWorkout.sets),
        reps: parseInt(newWorkout.reps),
        weight: newWorkout.weight ? parseFloat(newWorkout.weight) : null,
        notes: newWorkout.notes || null
      };
      await workoutsAPI.create(clientId, data);
      toast.success("Workout added!");
      setShowAddWorkout(false);
      setNewWorkout({
        date: new Date().toISOString().split('T')[0],
        exercise_name: "", sets: "", reps: "", weight: "", notes: ""
      });
      loadAllData();
    } catch (error) {
      toast.error("Failed to add workout");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type (JPG, PNG only)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPG and PNG formats are allowed");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await photosAPI.create(clientId, {
          date: new Date().toISOString().split('T')[0],
          photo_data: reader.result,
          photo_type: "progress",
          notes: null
        });
        toast.success("Photo uploaded!");
        loadAllData();
      } catch (error) {
        toast.error("Failed to upload photo");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.content) {
      toast.error("Please enter note content");
      return;
    }
    
    setSubmitting(true);
    try {
      await notesAPI.create(clientId, newNote);
      toast.success("Note added!");
      setShowAddNote(false);
      setNewNote({ content: "", category: "general", visibility: "private" });
      loadAllData();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProgress = async (entryId) => {
    // Show confirmation dialog
    setDeleteConfirm({ show: true, type: 'progress', id: entryId, name: 'progress entry' });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteConfirm;
    try {
      if (type === 'progress') {
        await progressAPI.delete(clientId, id);
        toast.success("Progress entry deleted");
      } else if (type === 'workout') {
        await workoutsAPI.delete(clientId, id);
        toast.success("Workout deleted");
      } else if (type === 'photo') {
        await photosAPI.delete(clientId, id);
        toast.success("Photo deleted");
      } else if (type === 'note') {
        await notesAPI.delete(clientId, id);
        toast.success("Note deleted");
      }
      loadAllData();
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setDeleteConfirm({ show: false, type: null, id: null, name: null });
    }
  };

  const handleDeleteWorkout = async (entryId) => {
    setDeleteConfirm({ show: true, type: 'workout', id: entryId, name: 'workout entry' });
  };

  const handleDeletePhoto = async (photoId) => {
    setDeleteConfirm({ show: true, type: 'photo', id: photoId, name: 'photo' });
  };

  const handleDeleteNote = async (noteId) => {
    setDeleteConfirm({ show: true, type: 'note', id: noteId, name: 'note' });
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type (JPG, PNG only)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPG and PNG formats are allowed");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Profile photo must be less than 5MB");
      return;
    }
    
    // Read file and open cropper
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset file input so same file can be selected again
    if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
  };

  // Upload the cropped photo blob
  const handleCroppedPhotoUpload = async (croppedBlob) => {
    setUploadingProfilePhoto(true);
    try {
      const formData = new FormData();
      const ext = croppedBlob.type === 'image/png' ? 'png' : 'jpg';
      formData.append('file', croppedBlob, `profile.${ext}`);
      
      await clientsAPI.uploadPhoto(clientId, formData);
      
      toast.success("Profile photo updated!");
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to upload profile photo");
    } finally {
      setUploadingProfilePhoto(false);
      setCropImage(null);
    }
  };

  // Remove client profile photo
  const handleRemoveClientPhoto = async () => {
    setUploadingProfilePhoto(true);
    try {
      await api.delete(`/clients/${clientId}/photo`);
      toast.success("Photo removed");
      loadAllData();
    } catch (error) {
      toast.error("Failed to remove photo");
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  // Create new weekly plan
  const handleCreatePlan = async () => {
    setCreatingPlan(true);
    try {
      const res = await plansAPI.create(clientId, {
        title: newPlanTitle,
        duration_weeks: newPlanDuration
      });
      setActivePlan(res.data);
      setAllPlans(prev => [res.data, ...prev]);
      setShowCreatePlan(false);
      setNewPlanTitle("Training Plan");
      setNewPlanDuration(4);
      toast.success("Plan created!");
    } catch {
      toast.error("Failed to create plan");
    } finally {
      setCreatingPlan(false);
    }
  };

  // Generate magic link for client
  const handleGenerateMagicLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await clientAuthAPI.generateMagicLink(clientId);
      setMagicLink(res.data.magic_link);
      toast.success("Magic link generated!");
    } catch {
      toast.error("Failed to generate magic link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyMagicLink = () => {
    navigator.clipboard.writeText(magicLink);
    toast.success("Link copied to clipboard!");
  };

  // Goal change handler
  const handleGoalChange = async () => {
    if (!newGoal || newGoal === client.goal) return;
    try {
      const res = await clientsAPI.changeGoal(clientId, newGoal, createNewPlanOnGoalChange);
      setClient(res.data);
      setShowGoalChange(false);
      toast.success("Goal updated! Previous progress history saved.");
      // Refresh goal history
      try {
        const histRes = await clientsAPI.getGoalHistory(clientId);
        setGoalHistory(histRes.data);
      } catch (e) { console.error('Failed to refresh goal history:', e); }
      if (createNewPlanOnGoalChange) {
        setActivePlan(null);
        try {
          const plansRes = await plansAPI.getAll(clientId);
          setAllPlans(plansRes.data || []);
        } catch (e) { console.error('Failed to refresh plans:', e); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change goal");
    }
  };

  // Client edit handler
  const handleEditClient = async () => {
    try {
      const payload = { ...editData };
      // Parse baseline measurement values to numbers
      ['baseline_chest','baseline_waist','baseline_hips','baseline_arms','baseline_thighs','baseline_calf','baseline_neck'].forEach(k => {
        payload[k] = payload[k] ? parseFloat(payload[k]) : null;
      });
      await clientsAPI.update(clientId, payload);
      const res = await clientsAPI.getOne(clientId);
      setClient(res.data);
      setShowEditClient(false);
      toast.success("Client updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  // Save coach notes
  const handleSaveCoachNotes = async () => {
    setSavingCoachNotes(true);
    try {
      await clientsAPI.update(clientId, { coach_notes: coachNotes });
      setClient(prev => ({ ...prev, coach_notes: coachNotes }));
      toast.success("Coach notes saved!");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSavingCoachNotes(false);
    }
  };

  // Helper function to format phone for WhatsApp
  const getWhatsAppLink = (phone) => {
    if (!phone) return null;
    // Remove all non-numeric characters except +
    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    return `https://wa.me/${cleanPhone.replace('+', '')}`;
  };

  const getGoalBadgeClass = (goal) => {
    switch (goal) {
      case "fat_loss": return "badge-fat-loss";
      case "muscle_gain": return "badge-muscle-gain";
      default: return "badge-maintenance";
    }
  };

  const getGoalLabel = (goal) => {
    const labels = {
      fat_loss: "Fat Loss", muscle_gain: "Muscle Gain", maintenance: "Maintenance",
      recomp: "Recomposition", strength: "Strength", general_fitness: "General Fitness",
      competition_prep: "Competition Prep", endurance: "Endurance"
    };
    return labels[goal] || goal?.replace(/_/g, ' ') || "—";
  };

  // Sort progress by date (oldest first for chart display - left to right)
  const sortedProgress = [...progress].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Build chart data: start from client's joining date with starting_weight
  const buildChartData = () => {
    if (!client) return [];
    const data = [];
    const startDate = client.start_date || client.created_at;
    
    // Add the joining point (starting weight at start_date)
    if (startDate && client.starting_weight) {
      const joinDate = new Date(startDate);
      // Only add if no progress entry exists on the same date
      const hasEntryOnStartDate = sortedProgress.some(p => {
        const pDate = new Date(p.date);
        return pDate.toDateString() === joinDate.toDateString();
      });
      if (!hasEntryOnStartDate) {
        data.push({
          date: joinDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          weight: client.starting_weight,
          isJoining: true
        });
      }
    }
    
    // Add all progress entries
    sortedProgress.forEach(p => {
      data.push({
        date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: p.weight
      });
    });
    
    return data;
  };
  
  const chartData = buildChartData();

  // UNIFIED: Single source of truth for weight calculation
  const { startWeight, currentWeight, change: weightChange } = calculateWeightProgress(client, progress);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Client not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-muted-foreground hover:text-foreground shrink-0 w-10 h-10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3 flex-1">
              {/* Profile Photo - Clickable to upload */}
              <div className="relative">
                <div 
                  className="relative cursor-pointer group"
                  onClick={() => profilePhotoInputRef.current?.click()}
                  data-testid="profile-photo-container"
                >
                  {client.profile_photo ? (
                    <img 
                      src={getImageUrl(client.profile_photo)} 
                      alt={client.name}
                      className="w-12 h-12 rounded-full object-cover group-hover:opacity-80 transition-opacity"
                      data-testid="client-profile-photo"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-12 h-12 rounded-full bg-[#00BFFF]/10 items-center justify-center group-hover:bg-[#00BFFF]/15 transition-colors ${client.profile_photo ? 'hidden' : 'flex'}`} data-testid="client-initials-avatar">
                    <span className="font-heading text-lg font-bold text-[#00BFFF]">
                      {getInitials(client.name)}
                    </span>
                  </div>
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                  {uploadingProfilePhoto && (
                    <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                      <div className="spinner" />
                    </div>
                  )}
                </div>
                {/* Remove photo button - only if photo exists */}
                {client.profile_photo && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveClientPhoto(); }}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive flex items-center justify-center hover:bg-destructive/80 transition-colors"
                    data-testid="remove-client-photo-btn"
                    title="Remove photo"
                  >
                    <Trash2 className="w-3 h-3 text-white" />
                  </button>
                )}
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-foreground">
                  {client.name}
                </h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={`${getGoalBadgeClass(client.goal)} text-xs`}>
                    {getGoalLabel(client.goal)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {client.age} yrs • {client.gender}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Info Row */}
          {(client.phone || client.email) && (
            <div className="flex items-center gap-3 mt-3 ml-14 flex-wrap">
              {client.phone && (
                <div className="flex items-center gap-2">
                  <a 
                    href={`tel:${client.phone}`}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="client-phone-link"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {client.phone}
                  </a>
                  <a
                    href={getWhatsAppLink(client.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors text-xs font-medium"
                    data-testid="client-whatsapp-link"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                </div>
              )}
              {client.email && (
                <a 
                  href={`mailto:${client.email}`}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="client-email-link"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {client.email}
                </a>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Hidden file inputs - MUST be outside tabs to always be in DOM */}
        <input
          type="file"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          capture="environment"
          ref={fileInputRef}
          onChange={handlePhotoUpload}
          className="hidden"
          data-testid="photo-input"
        />
        <input
          type="file"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          capture="environment"
          ref={profilePhotoInputRef}
          onChange={handleProfilePhotoUpload}
          className="hidden"
          data-testid="profile-photo-input"
        />

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card border-0" data-testid="current-weight-stat">
            <CardContent className="p-4 text-center">
              <Scale className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="font-heading text-3xl font-bold text-foreground">
                {currentWeight}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Current (kg)</p>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-0" data-testid="starting-weight-stat">
            <CardContent className="p-4 text-center">
              <Target className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
              <p className="font-heading text-3xl font-bold text-foreground">
                {startWeight}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Start (kg)</p>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-0" data-testid="weight-change-stat">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-neon-green mx-auto mb-2" />
              <p className={`font-heading text-3xl font-bold ${parseFloat(weightChange) < 0 ? 'text-neon-green' : parseFloat(weightChange) > 0 ? 'text-red-400' : 'text-foreground'}`}>
                {parseFloat(weightChange) > 0 ? '+' : ''}{weightChange}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Change (kg)</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - Progress Card, Client Portal & PDF Report */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={async () => {
              setLoadingProgressCard(true);
              try {
                await loadAllData();
                setShowProgressCard(true);
              } finally {
                setLoadingProgressCard(false);
              }
            }}
            disabled={loadingProgressCard}
            className="flex items-center justify-center gap-2 py-2.5 px-3 text-xs font-semibold tracking-wide uppercase rounded-xl transition-all disabled:opacity-50"
            style={{
              background: "rgba(0,191,255,0.06)",
              border: "1px solid rgba(0,191,255,0.20)",
              color: "#00BFFF",
            }}
            data-testid="share-progress-btn"
          >
            {loadingProgressCard ? (
              <div className="w-4 h-4 border-2 border-[#00BFFF]/30 border-t-[#00BFFF] rounded-full animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            {loadingProgressCard ? "..." : "Progress"}
          </button>
          <button
            onClick={handleGenerateMagicLink}
            disabled={generatingLink}
            className="flex items-center justify-center gap-2 py-2.5 px-3 text-xs font-semibold tracking-wide uppercase rounded-xl transition-all"
            style={{
              background: "rgba(34,197,94,0.06)",
              border: "1px solid rgba(34,197,94,0.20)",
              color: "#4ade80",
            }}
            data-testid="quick-magic-link-btn"
          >
            <Link2 className="w-4 h-4" />
            Portal
          </button>
          <button
            onClick={async () => {
              setDownloadingPdf(true);
              try {
                const res = await api.get(`/clients/${clientId}/report/pdf`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `FitTrack_${client?.name?.replace(/\s+/g, '_')}_Report.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("PDF report downloaded!");
              } catch { toast.error("Failed to generate PDF"); }
              finally { setDownloadingPdf(false); }
            }}
            disabled={downloadingPdf}
            className="flex items-center justify-center gap-2 py-2.5 px-3 text-xs font-semibold tracking-wide uppercase rounded-xl transition-all disabled:opacity-50"
            style={{
              background: "rgba(168,85,247,0.06)",
              border: "1px solid rgba(168,85,247,0.20)",
              color: "#A855F7",
            }}
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? (
              <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            PDF
          </button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex flex-wrap sm:grid sm:grid-cols-9 bg-white/[0.03] p-1.5 h-auto rounded-xl gap-1">
            <TabsTrigger
              value="profile"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 mr-1 hidden sm:block" />
              Profile
            </TabsTrigger>
            <TabsTrigger
              value="plans"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="plans-tab"
            >
              <ClipboardList className="w-4 h-4 mr-1 hidden sm:block" />
              Plans
            </TabsTrigger>
            <TabsTrigger
              value="progress"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="progress-tab"
            >
              <Scale className="w-4 h-4 mr-1 hidden sm:block" />
              Progress
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="payments-tab"
            >
              <IndianRupee className="w-4 h-4 mr-1 hidden sm:block" />
              Payments
            </TabsTrigger>
            <TabsTrigger
              value="diet"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="diet-tab"
            >
              <UtensilsCrossed className="w-4 h-4 mr-1 hidden sm:block" />
              Diet
            </TabsTrigger>
            <TabsTrigger
              value="attendance"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="attendance-tab"
            >
              <CalendarCheck className="w-4 h-4 mr-1 hidden sm:block" />
              Attend
            </TabsTrigger>
            <TabsTrigger
              value="workouts"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="workouts-tab"
            >
              <Dumbbell className="w-4 h-4 mr-1 hidden sm:block" />
              Log
            </TabsTrigger>
            <TabsTrigger
              value="photos"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="photos-tab"
            >
              <Camera className="w-4 h-4 mr-1 hidden sm:block" />
              Photos
            </TabsTrigger>
            <TabsTrigger
              value="notes"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-3 font-heading uppercase tracking-wide text-xs rounded-lg transition-all"
              data-testid="notes-tab"
            >
              <FileText className="w-4 h-4 mr-1 hidden sm:block" />
              Notes
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 animate-in">
            <Card className="glass-card border-0" data-testid="client-profile-info">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase">
                  Client Information
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="btn-secondary text-xs" data-testid="change-goal-btn"
                    onClick={() => { setNewGoal(client.goal); setShowGoalChange(true); }}>
                    <Target className="w-3 h-3 mr-1" /> Change Goal
                  </Button>
                  <Button size="sm" className="btn-premium text-white text-xs" data-testid="edit-client-btn"
                    onClick={() => {
                      setEditData({
                        name: client.name, age: client.age, gender: client.gender, phone: client.phone || "",
                        email: client.email || "", height: client.height || "", current_weight: client.current_weight || client.starting_weight,
                        target_weight: client.target_weight || "", activity_level: client.activity_level || "",
                        client_type: client.client_type || "", priority: client.priority || "normal",
                        medical_conditions: client.medical_conditions || "", injuries: client.injuries || "",
                        baseline_chest: client.baseline_chest || "", baseline_waist: client.baseline_waist || "",
                        baseline_hips: client.baseline_hips || "", baseline_arms: client.baseline_arms || "",
                        baseline_thighs: client.baseline_thighs || "", baseline_calf: client.baseline_calf || "",
                        baseline_neck: client.baseline_neck || ""
                      });
                      setShowEditClient(true);
                    }}>
                    <FileText className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name</p>
                    <p className="text-foreground font-medium">{client.name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Age</p>
                    <p className="text-foreground font-medium">{client.age} years</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Gender</p>
                    <p className="text-foreground font-medium capitalize">{client.gender}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Goal</p>
                    <Badge className={`${getGoalBadgeClass(client.goal)} text-xs`}>
                      {getGoalLabel(client.goal)}
                    </Badge>
                  </div>
                </div>

                <div className="border-t border-border/30 pt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Start Date</p>
                    <p className="text-foreground font-medium">
                      {new Date(client.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  {client.joining_date && (
                    <div className="space-y-1">
                      <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Joining Date</p>
                      <p className="text-foreground font-medium">
                        {new Date(client.joining_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  )}
                </div>

                {(client.phone || client.email) && (
                  <div className="border-t border-border/30 pt-4 space-y-3">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Contact</p>
                    {client.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <a href={`tel:${client.phone}`} className="text-foreground hover:text-primary transition-colors">
                          {client.phone}
                        </a>
                        <a
                          href={getWhatsAppLink(client.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors text-sm font-medium"
                          data-testid="profile-whatsapp-link"
                        >
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp
                        </a>
                      </div>
                    )}
                    {client.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <a href={`mailto:${client.email}`} className="text-foreground hover:text-primary transition-colors">
                          {client.email}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {client.notes && (
                  <div className="border-t border-border/30 pt-4">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Notes</p>
                    <p className="text-muted-foreground text-sm">{client.notes}</p>
                  </div>
                )}

                {/* Magic Link Section */}
                <div className="border-t border-border/30 pt-4 space-y-3">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Client Portal Access</p>
                  <p className="text-xs text-muted-foreground">Generate a secure link for {client.name} to view their plans, progress, and notes.</p>
                  {magicLink ? (
                    <div className="space-y-2">
                      <div className="p-3 rounded-xl bg-[#00BFFF]/5 border border-[#00BFFF]/10">
                        <p className="text-xs text-muted-foreground mb-1">Secure link generated</p>
                        <p className="text-sm text-foreground font-medium break-all" data-testid="magic-link-display">{magicLink}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          size="sm"
                          onClick={copyMagicLink}
                          className="h-9 btn-premium text-white text-xs uppercase tracking-wide"
                          data-testid="copy-magic-link-btn"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          Copy
                        </Button>
                        <a
                          href={magicLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 h-9 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-smooth font-semibold text-xs uppercase tracking-wide border border-primary/20"
                          data-testid="preview-magic-link-btn"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Preview
                        </a>
                        <a
                          href={`https://wa.me/${client.phone ? client.phone.replace(/[^0-9]/g, '') : ''}?text=${encodeURIComponent(`Hey ${client.name}! Check your fitness plan & progress here:\n${magicLink}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 h-9 rounded-xl bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-smooth font-semibold text-xs uppercase tracking-wide border border-green-500/20"
                          data-testid="whatsapp-share-magic-link"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Share
                        </a>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={handleGenerateMagicLink}
                      disabled={generatingLink}
                      className="w-full h-10 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl text-xs"
                      data-testid="generate-magic-link-btn"
                    >
                      {generatingLink ? <div className="spinner" /> : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Generate Client Login Link
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Coach Notes (Private) */}
            <Card className="glass-card border-0" data-testid="coach-notes-section">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="font-heading text-sm tracking-wide uppercase">Coach Notes</CardTitle>
                  <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-400">Private</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={coachNotes}
                  onChange={(e) => setCoachNotes(e.target.value)}
                  placeholder="Internal coaching notes — only visible to you..."
                  className="bg-secondary/50 border-transparent min-h-[80px] text-sm"
                  data-testid="coach-notes-textarea"
                />
                <Button size="sm" onClick={handleSaveCoachNotes} disabled={savingCoachNotes} className="btn-premium text-white text-xs" data-testid="save-coach-notes-btn">
                  {savingCoachNotes ? <div className="spinner" /> : "Save Notes"}
                </Button>
              </CardContent>
            </Card>

            {/* Goal History */}
            {goalHistory.length > 0 && (
              <Card className="glass-card border-0" data-testid="goal-history-section">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm tracking-wide uppercase flex items-center gap-2">
                    <History className="w-4 h-4 text-[#00BFFF]" /> Goal History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {goalHistory.map((gh) => (
                    <div key={gh.changed_at} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04] text-xs">
                      <Badge className="badge-maintenance text-[10px]">{gh.previous_goal?.replace(/_/g, ' ')}</Badge>
                      <ArrowLeft className="w-3 h-3 text-muted-foreground rotate-180" />
                      <Badge className="bg-[#00BFFF]/15 text-[#00BFFF] text-[10px]">{gh.new_goal?.replace(/_/g, ' ')}</Badge>
                      <span className="text-muted-foreground ml-auto">{new Date(gh.changed_at).toLocaleDateString('en-US', {month:'short',day:'numeric'})}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-4 animate-in">
            {/* Weight Chart */}
            {chartData.length > 1 && (
              <Card className="glass-card border-0" data-testid="weight-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg tracking-wide uppercase">
                    Weight Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="date" stroke="#a1a1aa" fontSize={12} />
                        <YAxis stroke="#a1a1aa" fontSize={12} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '12px' }}
                          labelStyle={{ color: '#fafafa' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="weight"
                          stroke="#007AFF"
                          strokeWidth={3}
                          dot={{ fill: '#007AFF', r: 5, strokeWidth: 2, stroke: '#0A0A0A' }}
                          activeDot={{ r: 7, fill: '#007AFF', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Add Progress Button */}
            <Dialog open={showAddProgress} onOpenChange={setShowAddProgress}>
              <DialogTrigger asChild>
                <Button
                  className="w-full h-14 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                  data-testid="add-progress-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Progress Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl tracking-tight uppercase">
                    Add Progress Entry
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddProgress} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Date
                      </Label>
                      <Input
                        type="date"
                        value={newProgress.date}
                        onChange={(e) => setNewProgress({ ...newProgress, date: e.target.value })}
                        className="h-12 bg-secondary/50 border-transparent"
                        data-testid="progress-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Weight (kg) *
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.weight}
                        onChange={(e) => setNewProgress({ ...newProgress, weight: e.target.value })}
                        placeholder="70.5"
                        className="h-12 bg-secondary/50 border-transparent"
                        data-testid="progress-weight"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Body Fat % (Optional)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newProgress.body_fat_percent}
                      onChange={(e) => setNewProgress({ ...newProgress, body_fat_percent: e.target.value })}
                      placeholder="15.5"
                      className="h-12 bg-secondary/50 border-transparent"
                      data-testid="progress-bodyfat"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Measurements (cm) - Optional
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.chest}
                        onChange={(e) => setNewProgress({ ...newProgress, chest: e.target.value })}
                        placeholder="Chest"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.waist}
                        onChange={(e) => setNewProgress({ ...newProgress, waist: e.target.value })}
                        placeholder="Waist"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.hips}
                        onChange={(e) => setNewProgress({ ...newProgress, hips: e.target.value })}
                        placeholder="Hips"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.arms}
                        onChange={(e) => setNewProgress({ ...newProgress, arms: e.target.value })}
                        placeholder="Arms"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.thighs}
                        onChange={(e) => setNewProgress({ ...newProgress, thighs: e.target.value })}
                        placeholder="Thighs"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.calf}
                        onChange={(e) => setNewProgress({ ...newProgress, calf: e.target.value })}
                        placeholder="Calf"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={newProgress.neck}
                        onChange={(e) => setNewProgress({ ...newProgress, neck: e.target.value })}
                        placeholder="Neck"
                        className="h-10 bg-secondary/50 border-transparent text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Notes (Optional)
                    </Label>
                    <Textarea
                      value={newProgress.notes}
                      onChange={(e) => setNewProgress({ ...newProgress, notes: e.target.value })}
                      placeholder="Any observations..."
                      className="bg-secondary/50 border-transparent min-h-[80px]"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                    disabled={submitting}
                    data-testid="submit-progress"
                  >
                    {submitting ? <div className="spinner" /> : "Save Progress"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Progress History */}
            <div className="space-y-3">
              {progress.length === 0 ? (
                <Card className="empty-state rounded-2xl">
                  <CardContent className="p-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                      <Scale className="w-8 h-8 empty-state-icon" />
                    </div>
                    <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                      No Progress Entries
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      Start tracking weight and measurements to visualize your client's transformation journey.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                [...progress].reverse().map((entry) => (
                  <Card key={entry.id} className="glass-card border-0" data-testid={`progress-entry-${entry.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="text-sm text-muted-foreground">
                              {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <p className="font-heading text-3xl font-bold text-foreground">
                            {entry.weight} <span className="text-lg text-muted-foreground">kg</span>
                          </p>
                          {entry.body_fat_percent && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Body Fat: <span className="text-foreground font-medium">{entry.body_fat_percent}%</span>
                            </p>
                          )}
                          {(entry.chest || entry.waist || entry.hips || entry.arms || entry.thighs || entry.calf || entry.neck) && (
                            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                              {entry.chest && <span>Chest: <span className="text-foreground">{entry.chest}cm</span></span>}
                              {entry.waist && <span>Waist: <span className="text-foreground">{entry.waist}cm</span></span>}
                              {entry.hips && <span>Hips: <span className="text-foreground">{entry.hips}cm</span></span>}
                              {entry.arms && <span>Arms: <span className="text-foreground">{entry.arms}cm</span></span>}
                              {entry.thighs && <span>Thighs: <span className="text-foreground">{entry.thighs}cm</span></span>}
                              {entry.calf && <span>Calf: <span className="text-foreground">{entry.calf}cm</span></span>}
                              {entry.neck && <span>Neck: <span className="text-foreground">{entry.neck}cm</span></span>}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteProgress(entry.id)}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl"
                          data-testid={`delete-progress-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Weekly Plans Tab */}
          <TabsContent value="plans" className="space-y-4 animate-in">
            {/* Create Plan / Active Plan Header */}
            {!activePlan ? (
              <Card className="glass-card border-0">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                    <ClipboardList className="w-8 h-8 text-[#00BFFF]/40" />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                    No Active Plan
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-6">
                    Create a structured weekly plan with workouts, diet, and coach notes.
                  </p>
                  <Button
                    onClick={() => setShowCreatePlan(true)}
                    className="btn-premium text-white font-semibold tracking-wide uppercase rounded-xl h-12 px-8"
                    data-testid="create-plan-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Plan Info Bar */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <h3 className="font-heading text-base font-bold uppercase tracking-wide text-foreground" data-testid="active-plan-title">
                      {activePlan.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {activePlan.duration_weeks} weeks &middot; Created {new Date(activePlan.created_at).toLocaleDateString('en-US', {month:'short', day:'numeric'})}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPlanHistory(!showPlanHistory)}
                      className="btn-secondary text-xs"
                      data-testid="plan-history-btn"
                    >
                      <History className="w-3 h-3 mr-1" /> History
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowCreatePlan(true)}
                      className="btn-premium text-white text-xs"
                      data-testid="new-plan-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> New Plan
                    </Button>
                  </div>
                </div>

                {/* Plan History Dropdown */}
                {showPlanHistory && allPlans.length > 0 && (
                  <Card className="glass-card border-0" data-testid="plan-history-list">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                        <History className="w-4 h-4 text-[#00BFFF]" /> Plan History
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {allPlans.map((p) => (
                        <div
                          key={p.id}
                          className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-smooth ${
                            p.id === activePlan.id
                              ? 'bg-[#00BFFF]/10 border border-[#00BFFF]/30'
                              : 'bg-white/[0.02] border border-white/[0.04] hover:border-[#00BFFF]/15'
                          }`}
                          onClick={() => { setActivePlan(p); setShowPlanHistory(false); }}
                          data-testid={`plan-${p.id}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-foreground">{p.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {p.duration_weeks}w &middot; {new Date(p.created_at).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}
                            </p>
                          </div>
                          {p.is_active && (
                            <Badge className="bg-[#00BFFF]/20 text-[#00BFFF] border-[#00BFFF]/30 text-xs">Active</Badge>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Weekly Plan Editor */}
                <WeeklyPlanEditor
                  clientId={clientId}
                  plan={activePlan}
                  onUpdate={(updated) => {
                    setActivePlan(updated);
                    setAllPlans(prev => prev.map(p => p.id === updated.id ? updated : p));
                  }}
                />
              </>
            )}
          </TabsContent>

          {/* Create Plan Dialog */}
          <Dialog open={showCreatePlan} onOpenChange={setShowCreatePlan}>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl tracking-tight uppercase">
                  Create Weekly Plan
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  Set up a structured training program for {client.name}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Plan Title</Label>
                  <Input
                    value={newPlanTitle}
                    onChange={(e) => setNewPlanTitle(e.target.value)}
                    placeholder="e.g., Fat Loss Phase 1"
                    className="h-12 bg-secondary/50 border-transparent"
                    data-testid="plan-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Duration</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[2, 4, 8, 12].map(w => (
                      <button
                        key={w}
                        onClick={() => setNewPlanDuration(w)}
                        className={`py-3 rounded-xl text-sm font-bold transition-smooth ${
                          newPlanDuration === w
                            ? 'bg-[#00BFFF]/20 text-[#00BFFF] border border-[#00BFFF]/30'
                            : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'
                        }`}
                        data-testid={`duration-${w}`}
                      >
                        {w}w
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleCreatePlan}
                  disabled={creatingPlan || !newPlanTitle.trim()}
                  className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                  data-testid="confirm-create-plan-btn"
                >
                  {creatingPlan ? <div className="spinner" /> : "Create Plan"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Workouts Tab */}
          <TabsContent value="workouts" className="space-y-4 animate-in">
            <Dialog open={showAddWorkout} onOpenChange={setShowAddWorkout}>
              <DialogTrigger asChild>
                <Button
                  className="w-full h-14 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                  data-testid="add-workout-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Workout Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl tracking-tight uppercase">
                    Add Workout Entry
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddWorkout} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Date
                    </Label>
                    <Input
                      type="date"
                      value={newWorkout.date}
                      onChange={(e) => setNewWorkout({ ...newWorkout, date: e.target.value })}
                      className="h-12 bg-secondary/50 border-transparent"
                      data-testid="workout-date"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Exercise Name *
                    </Label>
                    <Input
                      value={newWorkout.exercise_name}
                      onChange={(e) => setNewWorkout({ ...newWorkout, exercise_name: e.target.value })}
                      placeholder="e.g., Bench Press, Squats"
                      className="h-12 bg-secondary/50 border-transparent"
                      data-testid="workout-exercise"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Sets *
                      </Label>
                      <Input
                        type="number"
                        value={newWorkout.sets}
                        onChange={(e) => setNewWorkout({ ...newWorkout, sets: e.target.value })}
                        placeholder="3"
                        className="h-12 bg-secondary/50 border-transparent"
                        data-testid="workout-sets"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Reps *
                      </Label>
                      <Input
                        type="number"
                        value={newWorkout.reps}
                        onChange={(e) => setNewWorkout({ ...newWorkout, reps: e.target.value })}
                        placeholder="10"
                        className="h-12 bg-secondary/50 border-transparent"
                        data-testid="workout-reps"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Weight (kg)
                      </Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={newWorkout.weight}
                        onChange={(e) => setNewWorkout({ ...newWorkout, weight: e.target.value })}
                        placeholder="60"
                        className="h-12 bg-secondary/50 border-transparent"
                        data-testid="workout-weight"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                    disabled={submitting}
                    data-testid="submit-workout"
                  >
                    {submitting ? <div className="spinner" /> : "Save Workout"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Workouts List */}
            <div className="space-y-3">
              {workouts.length === 0 ? (
                <Card className="empty-state rounded-2xl">
                  <CardContent className="p-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                      <Dumbbell className="w-8 h-8 empty-state-icon" />
                    </div>
                    <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                      No Workout Entries
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      Log exercises, sets, and reps to track your client's training progress over time.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                workouts.map((entry) => (
                  <Card key={entry.id} className="glass-card border-0" data-testid={`workout-entry-${entry.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="text-sm text-muted-foreground">
                              {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <p className="font-heading text-xl font-bold text-foreground">
                            {entry.exercise_name}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                              {entry.sets} × {entry.reps}
                            </span>
                            {entry.weight && (
                              <span className="text-sm text-muted-foreground">
                                @ <span className="text-foreground font-medium">{entry.weight} kg</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteWorkout(entry.id)}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl"
                          data-testid={`delete-workout-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-4 animate-in">
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-14 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
              data-testid="upload-photo-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              Upload Progress Photo
            </Button>
            
            {/* Photos Gallery */}
            {photos.length === 0 ? (
              <Card className="empty-state rounded-2xl">
                <CardContent className="p-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-8 h-8 empty-state-icon" />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                    No Progress Photos
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                    Upload before and after photos to visually document your client's transformation.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="photo-grid">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative group rounded-xl overflow-hidden" data-testid={`photo-${photo.id}`}>
                    <img
                      src={photo.photo_data}
                      alt={`Progress ${photo.date}`}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="text-white hover:text-destructive hover:bg-destructive/20 rounded-xl"
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                    <p className="absolute bottom-2 left-2 text-xs text-white bg-black/60 px-2 py-1 rounded-lg">
                      {new Date(photo.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="space-y-4 animate-in">
            <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
              <DialogTrigger asChild>
                <Button
                  className="w-full h-14 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Coach Note
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl tracking-tight uppercase">
                    Add Coach Note
                  </DialogTitle>
                  <DialogDescription className="text-muted-foreground text-sm">
                    Add coaching observations. Choose visibility to control client access.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddNote} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Category
                    </Label>
                    <Select
                      value={newNote.category}
                      onValueChange={(value) => setNewNote({ ...newNote, category: value })}
                    >
                      <SelectTrigger className="h-12 bg-secondary/50 border-transparent" data-testid="note-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="diet">Diet</SelectItem>
                        <SelectItem value="injury">Injury</SelectItem>
                        <SelectItem value="observation">Observation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Visibility Toggle */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Visibility
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setNewNote({ ...newNote, visibility: "private" })}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-smooth ${
                          newNote.visibility === "private"
                            ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30'
                            : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-orange-500/15'
                        }`}
                        data-testid="visibility-private"
                      >
                        <EyeOff className="w-4 h-4" /> Private
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewNote({ ...newNote, visibility: "client_visible" })}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-smooth ${
                          newNote.visibility === "client_visible"
                            ? 'bg-[#00BFFF]/15 text-[#00BFFF] border border-[#00BFFF]/30'
                            : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'
                        }`}
                        data-testid="visibility-client"
                      >
                        <Eye className="w-4 h-4" /> Client Visible
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                      Note *
                    </Label>
                    <Textarea
                      value={newNote.content}
                      onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                      placeholder="Enter your notes here..."
                      className="bg-secondary/50 border-transparent min-h-[120px]"
                      data-testid="note-content"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                    disabled={submitting}
                    data-testid="submit-note"
                  >
                    {submitting ? <div className="spinner" /> : "Save Note"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Notes List */}
            <div className="space-y-3">
              {notes.length === 0 ? (
                <Card className="empty-state rounded-2xl">
                  <CardContent className="p-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 empty-state-icon" />
                    </div>
                    <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                      No Coach Notes
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      Add observations about diet, injuries, or general coaching notes for future reference.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                notes.map((note) => (
                  <Card key={note.id} className="glass-card border-0" data-testid={`note-${note.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className={`text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full ${
                              note.category === 'diet' ? 'bg-green-500/10 text-green-400' :
                              note.category === 'injury' ? 'bg-red-500/10 text-red-400' :
                              note.category === 'observation' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-blue-500/10 text-blue-400'
                            }`}>
                              {note.category}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${
                              note.visibility === 'client_visible'
                                ? 'bg-[#00BFFF]/10 text-[#00BFFF]'
                                : 'bg-orange-500/10 text-orange-400'
                            }`} data-testid={`note-visibility-${note.id}`}>
                              {note.visibility === 'client_visible' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              {note.visibility === 'client_visible' ? 'Client' : 'Private'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-foreground whitespace-pre-wrap leading-relaxed">{note.content}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl ml-3"
                          data-testid={`delete-note-${note.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4 animate-in">
            <Card className="glass-card border-0" data-testid="client-payments-section">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase">
                  Payment History
                </CardTitle>
                <Dialog open={showRecordPayment} onOpenChange={setShowRecordPayment}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="btn-premium text-white font-bold" data-testid="record-payment-btn">
                      <Plus className="w-4 h-4 mr-1" /> Record Payment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-sm">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-lg uppercase">Record Payment</DialogTitle>
                      <DialogDescription className="text-muted-foreground text-sm">Record a payment received from {client?.name}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) { toast.error("Enter valid amount"); return; }
                      setSubmitting(true);
                      try {
                        const res = await clientPaymentsAPI.record(clientId, {
                          amount: parseFloat(newPayment.amount),
                          mode: newPayment.mode,
                          note: newPayment.note,
                          date: newPayment.date,
                        });
                        setClientPayments([res.data, ...clientPayments]);
                        setClient({ ...client, payment_status: "paid" });
                        setShowRecordPayment(false);
                        setNewPayment({ amount: "", mode: "cash", note: "", date: new Date().toISOString().split('T')[0] });
                        toast.success(`₹${newPayment.amount} payment recorded!`);
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed to record payment"); }
                      finally { setSubmitting(false); }
                    }} className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Amount (₹) *</Label>
                        <Input type="number" placeholder="e.g. 5000" value={newPayment.amount}
                          onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                          className="h-12 bg-white/[0.03] border-white/[0.06] rounded-xl" data-testid="payment-amount-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Mode</Label>
                          <Select value={newPayment.mode} onValueChange={(v) => setNewPayment({ ...newPayment, mode: v })}>
                            <SelectTrigger className="h-12 bg-white/[0.03] border-white/[0.06] rounded-xl" data-testid="payment-mode-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="upi">UPI</SelectItem>
                              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                              <SelectItem value="card">Card</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Date</Label>
                          <Input type="date" value={newPayment.date}
                            onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                            className="h-12 bg-white/[0.03] border-white/[0.06] rounded-xl" data-testid="payment-date-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Note (optional)</Label>
                        <Input placeholder="e.g. March fees" value={newPayment.note}
                          onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
                          className="h-12 bg-white/[0.03] border-white/[0.06] rounded-xl" data-testid="payment-note-input" />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full h-12 btn-premium text-white font-bold uppercase rounded-xl" data-testid="payment-submit-btn">
                        {submitting ? <div className="spinner" /> : "Record Payment"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {/* Payment Status Summary */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                    <p className="text-xs text-muted-foreground uppercase font-bold">Plan Fee</p>
                    <p className="text-lg font-extrabold text-foreground mt-1">₹{client?.plan_price || 0}</p>
                  </div>
                  <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                    <p className="text-xs text-muted-foreground uppercase font-bold">Status</p>
                    <Badge className={`mt-1 ${
                      client?.payment_status === "paid" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                      client?.payment_status === "overdue" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                      "bg-amber-500/20 text-amber-400 border-amber-500/30"
                    }`} data-testid="payment-status-badge">
                      {client?.payment_status || "Pending"}
                    </Badge>
                  </div>
                  <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                    <p className="text-xs text-muted-foreground uppercase font-bold">Total Paid</p>
                    <p className="text-lg font-extrabold text-green-400 mt-1">₹{clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0)}</p>
                  </div>
                </div>

                {/* Due Date */}
                {client?.payment_due_date && (
                  <div className="flex items-center gap-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl mb-4">
                    <Calendar className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-amber-400 font-semibold">Due: {new Date(client.payment_due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                )}

                {/* Payment Records */}
                {clientPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <IndianRupee className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No payments recorded yet</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Click "Record Payment" to add one</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clientPayments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl border border-white/[0.04] hover:bg-white/[0.04] transition-colors" data-testid={`payment-record-${p.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <IndianRupee className="w-4 h-4 text-green-400" />
                          </div>
                          <div>
                            <p className="font-bold text-foreground text-sm">₹{p.amount}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(p.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {p.mode && <span className="ml-1 capitalize">· {p.mode.replace('_', ' ')}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.note && <span className="text-xs text-muted-foreground/60 hidden sm:inline">{p.note}</span>}
                          <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-destructive"
                            onClick={async () => {
                              try {
                                await clientPaymentsAPI.remove(clientId, p.id);
                                setClientPayments(clientPayments.filter(x => x.id !== p.id));
                                toast.success("Payment record deleted");
                              } catch { toast.error("Failed to delete"); }
                            }} data-testid={`delete-payment-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Diet Plan Tab */}
          <TabsContent value="diet" className="space-y-4 animate-in">
            <Card className="glass-card border-0" data-testid="diet-plans-section">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase">
                  Diet Plans
                </CardTitle>
                <Button size="sm" className="btn-premium text-white font-bold"
                  onClick={() => {
                    setEditingDietPlan(null);
                    setDietForm({
                      name: "Diet Plan", notes: "",
                      total_calories: "", total_protein: "", total_carbs: "", total_fat: "",
                      meals: { breakfast: [], lunch: [], snack: [], dinner: [] }
                    });
                    setShowAddDietPlan(true);
                  }}
                  data-testid="add-diet-plan-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Plan
                </Button>
              </CardHeader>
              <CardContent>
                {dietPlans.length === 0 ? (
                  <div className="text-center py-8">
                    <UtensilsCrossed className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No diet plans yet</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Create a personalized diet plan for this client</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dietPlans.map((dp) => (
                      <div key={dp.id} className={`p-4 rounded-xl border ${dp.is_active ? 'border-green-500/30 bg-green-500/5' : 'border-white/[0.06] bg-white/[0.02]'}`} data-testid={`diet-plan-${dp.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-foreground text-sm">{dp.name}</h4>
                            {dp.is_active && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">Active</Badge>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-primary"
                              onClick={() => {
                                setEditingDietPlan(dp);
                                setDietForm({
                                  name: dp.name, notes: dp.notes || "",
                                  total_calories: dp.total_calories || "", total_protein: dp.total_protein || "",
                                  total_carbs: dp.total_carbs || "", total_fat: dp.total_fat || "",
                                  meals: dp.meals || { breakfast: [], lunch: [], snack: [], dinner: [] }
                                });
                                setShowAddDietPlan(true);
                              }} data-testid={`edit-diet-${dp.id}`}>
                              <FileText className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                try {
                                  await dietPlansAPI.remove(clientId, dp.id);
                                  setDietPlans(dietPlans.filter(x => x.id !== dp.id));
                                  toast.success("Diet plan deleted");
                                } catch { toast.error("Failed to delete"); }
                              }} data-testid={`delete-diet-${dp.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Macro Summary */}
                        {(dp.total_calories > 0 || dp.total_protein > 0) && (
                          <div className="grid grid-cols-4 gap-2 mb-3">
                            <div className="p-2 bg-white/[0.03] rounded-lg text-center">
                              <p className="text-[10px] text-muted-foreground uppercase">Cal</p>
                              <p className="text-sm font-bold text-foreground">{dp.total_calories || 0}</p>
                            </div>
                            <div className="p-2 bg-white/[0.03] rounded-lg text-center">
                              <p className="text-[10px] text-muted-foreground uppercase">Protein</p>
                              <p className="text-sm font-bold text-red-400">{dp.total_protein || 0}g</p>
                            </div>
                            <div className="p-2 bg-white/[0.03] rounded-lg text-center">
                              <p className="text-[10px] text-muted-foreground uppercase">Carbs</p>
                              <p className="text-sm font-bold text-amber-400">{dp.total_carbs || 0}g</p>
                            </div>
                            <div className="p-2 bg-white/[0.03] rounded-lg text-center">
                              <p className="text-[10px] text-muted-foreground uppercase">Fat</p>
                              <p className="text-sm font-bold text-blue-400">{dp.total_fat || 0}g</p>
                            </div>
                          </div>
                        )}

                        {/* Meals */}
                        {dp.meals && Object.entries(dp.meals).map(([mealType, items]) => (
                          items && items.length > 0 && (
                            <div key={mealType} className="mb-2">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1 capitalize">{mealType}</p>
                              <div className="space-y-1">
                                {items.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs px-2 py-1 bg-white/[0.02] rounded-lg">
                                    <span className="text-foreground">{item.name} {item.quantity && <span className="text-muted-foreground">({item.quantity})</span>}</span>
                                    <span className="text-muted-foreground">{item.calories ? `${item.calories} cal` : ''}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        ))}

                        {dp.notes && <p className="text-xs text-muted-foreground mt-2 italic">{dp.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add/Edit Diet Plan Dialog */}
            <Dialog open={showAddDietPlan} onOpenChange={(open) => { if (!open) { setShowAddDietPlan(false); setAddingMealTo(null); } }}>
              <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto pt-8 sm:pt-6">
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg uppercase">{editingDietPlan ? "Edit" : "New"} Diet Plan</DialogTitle>
                  <DialogDescription className="text-muted-foreground text-sm">Set meals, macros and calories for {client?.name}</DialogDescription>
                </DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!dietForm.name) { toast.error("Enter plan name"); return; }
                  setSubmitting(true);
                  try {
                    const payload = {
                      name: dietForm.name, notes: dietForm.notes,
                      total_calories: parseFloat(dietForm.total_calories) || 0,
                      total_protein: parseFloat(dietForm.total_protein) || 0,
                      total_carbs: parseFloat(dietForm.total_carbs) || 0,
                      total_fat: parseFloat(dietForm.total_fat) || 0,
                      meals: dietForm.meals, is_active: true,
                    };
                    if (editingDietPlan) {
                      const res = await dietPlansAPI.update(clientId, editingDietPlan.id, payload);
                      setDietPlans(dietPlans.map(d => d.id === editingDietPlan.id ? res.data : d));
                      toast.success("Diet plan updated!");
                    } else {
                      const res = await dietPlansAPI.create(clientId, payload);
                      setDietPlans([res.data, ...dietPlans.map(d => ({ ...d, is_active: false }))]);
                      toast.success("Diet plan created!");
                    }
                    setShowAddDietPlan(false);
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
                  finally { setSubmitting(false); }
                }} className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Plan Name</Label>
                    <Input value={dietForm.name} onChange={(e) => setDietForm({ ...dietForm, name: e.target.value })}
                      className="h-11 bg-white/[0.03] border-white/[0.06] rounded-xl" data-testid="diet-plan-name-input" />
                  </div>

                  {/* Macro Targets */}
                  <div>
                    <Label className="text-xs font-bold tracking-widest uppercase text-primary mb-2 block">Daily Targets</Label>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Calories</Label>
                        <Input type="number" placeholder="2000" value={dietForm.total_calories}
                          onChange={(e) => setDietForm({ ...dietForm, total_calories: e.target.value })}
                          className="h-10 bg-white/[0.03] border-white/[0.06] rounded-lg text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Protein (g)</Label>
                        <Input type="number" placeholder="140" value={dietForm.total_protein}
                          onChange={(e) => setDietForm({ ...dietForm, total_protein: e.target.value })}
                          className="h-10 bg-white/[0.03] border-white/[0.06] rounded-lg text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Carbs (g)</Label>
                        <Input type="number" placeholder="200" value={dietForm.total_carbs}
                          onChange={(e) => setDietForm({ ...dietForm, total_carbs: e.target.value })}
                          className="h-10 bg-white/[0.03] border-white/[0.06] rounded-lg text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Fat (g)</Label>
                        <Input type="number" placeholder="60" value={dietForm.total_fat}
                          onChange={(e) => setDietForm({ ...dietForm, total_fat: e.target.value })}
                          className="h-10 bg-white/[0.03] border-white/[0.06] rounded-lg text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Meals */}
                  {["breakfast", "lunch", "snack", "dinner"].map((mealType) => (
                    <div key={mealType} className="p-3 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-primary capitalize">{mealType}</p>
                        <button type="button" onClick={() => setAddingMealTo(addingMealTo === mealType ? null : mealType)}
                          className="text-xs text-primary hover:underline flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Add
                        </button>
                      </div>

                      {/* Meal items */}
                      {(dietForm.meals[mealType] || []).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white/[0.02] rounded-lg mb-1">
                          <span className="text-foreground">{item.name} {item.quantity && <span className="text-muted-foreground">({item.quantity})</span>}</span>
                          <div className="flex items-center gap-2">
                            {item.calories > 0 && <span className="text-muted-foreground">{item.calories} cal</span>}
                            <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => {
                              const updated = { ...dietForm.meals };
                              updated[mealType] = updated[mealType].filter((_, i) => i !== idx);
                              setDietForm({ ...dietForm, meals: updated });
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add meal item form */}
                      {addingMealTo === mealType && (
                        <div className="mt-2 p-2 bg-white/[0.03] rounded-lg space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Food name" value={mealInput.name} onChange={(e) => setMealInput({ ...mealInput, name: e.target.value })}
                              className="h-9 text-xs bg-white/[0.03] border-white/[0.06] rounded-lg" />
                            <Input placeholder="Qty (e.g. 1 bowl)" value={mealInput.quantity} onChange={(e) => setMealInput({ ...mealInput, quantity: e.target.value })}
                              className="h-9 text-xs bg-white/[0.03] border-white/[0.06] rounded-lg" />
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            <Input type="number" placeholder="Cal" value={mealInput.calories} onChange={(e) => setMealInput({ ...mealInput, calories: e.target.value })}
                              className="h-8 text-[10px] bg-white/[0.03] border-white/[0.06] rounded-lg" />
                            <Input type="number" placeholder="P(g)" value={mealInput.protein} onChange={(e) => setMealInput({ ...mealInput, protein: e.target.value })}
                              className="h-8 text-[10px] bg-white/[0.03] border-white/[0.06] rounded-lg" />
                            <Input type="number" placeholder="C(g)" value={mealInput.carbs} onChange={(e) => setMealInput({ ...mealInput, carbs: e.target.value })}
                              className="h-8 text-[10px] bg-white/[0.03] border-white/[0.06] rounded-lg" />
                            <Input type="number" placeholder="F(g)" value={mealInput.fat} onChange={(e) => setMealInput({ ...mealInput, fat: e.target.value })}
                              className="h-8 text-[10px] bg-white/[0.03] border-white/[0.06] rounded-lg" />
                          </div>
                          <Button type="button" size="sm" className="w-full h-8 text-xs"
                            onClick={() => {
                              if (!mealInput.name) { toast.error("Enter food name"); return; }
                              const updated = { ...dietForm.meals };
                              updated[mealType] = [...(updated[mealType] || []), {
                                name: mealInput.name, quantity: mealInput.quantity,
                                calories: parseFloat(mealInput.calories) || 0,
                                protein: parseFloat(mealInput.protein) || 0,
                                carbs: parseFloat(mealInput.carbs) || 0,
                                fat: parseFloat(mealInput.fat) || 0,
                              }];
                              setDietForm({ ...dietForm, meals: updated });
                              setMealInput({ name: "", calories: "", protein: "", carbs: "", fat: "", quantity: "" });
                            }}>
                            Add Item
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Notes</Label>
                    <Textarea value={dietForm.notes} onChange={(e) => setDietForm({ ...dietForm, notes: e.target.value })}
                      placeholder="Special instructions, allergies, preferences..."
                      className="bg-white/[0.03] border-white/[0.06] rounded-xl min-h-[60px]" />
                  </div>

                  <Button type="submit" disabled={submitting} className="w-full h-12 btn-premium text-white font-bold uppercase rounded-xl" data-testid="diet-plan-submit-btn">
                    {submitting ? <div className="spinner" /> : (editingDietPlan ? "Update Plan" : "Create Plan")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4 animate-in">
            <Card className="glass-card border-0" data-testid="attendance-section">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase">
                  Attendance
                </CardTitle>
                <div className="flex gap-2">
                  {["present", "absent", "late"].map((s) => {
                    const todayRecord = attendance.find(a => a.date === new Date().toISOString().split('T')[0]);
                    const isActive = todayRecord?.status === s;
                    const colors = { present: "bg-green-500/20 text-green-400 border-green-500/30", absent: "bg-red-500/20 text-red-400 border-red-500/30", late: "bg-amber-500/20 text-amber-400 border-amber-500/30" };
                    return (
                      <Button key={s} size="sm" variant="outline" disabled={markingAttendance}
                        className={`text-xs capitalize ${isActive ? colors[s] + " border" : "border-white/10"}`}
                        onClick={async () => {
                          setMarkingAttendance(true);
                          try {
                            await attendanceAPI.mark(clientId, { status: s });
                            const res = await attendanceAPI.getAll(clientId);
                            setAttendance(res.data || []);
                            const statsRes = await attendanceAPI.getStats(clientId).catch(() => ({ data: null }));
                            setAttendanceStats(statsRes.data);
                            toast.success(`Marked ${s} for today`);
                          } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                          finally { setMarkingAttendance(false); }
                        }}
                        data-testid={`mark-${s}-btn`}>
                        {isActive && <Check className="w-3 h-3 mr-1" />}
                        {s}
                      </Button>
                    );
                  })}
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats Summary */}
                {attendanceStats && attendanceStats.total_records > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Rate</p>
                      <p className="text-lg font-extrabold text-green-400">{attendanceStats.attendance_rate}%</p>
                    </div>
                    <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Present</p>
                      <p className="text-lg font-extrabold text-foreground">{attendanceStats.present}</p>
                    </div>
                    <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Absent</p>
                      <p className="text-lg font-extrabold text-red-400">{attendanceStats.absent}</p>
                    </div>
                    <div className="p-3 bg-white/[0.03] rounded-xl text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Streak</p>
                      <p className="text-lg font-extrabold text-amber-400">{attendanceStats.current_streak}</p>
                    </div>
                  </div>
                )}

                {/* Attendance Records */}
                {attendance.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarCheck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No attendance records yet</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Mark today's attendance above</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {attendance.slice(0, 30).map((a) => {
                      const statusColors = { present: "bg-green-500/20 text-green-400", absent: "bg-red-500/20 text-red-400", late: "bg-amber-500/20 text-amber-400" };
                      return (
                        <div key={a.date} className="flex items-center justify-between p-2.5 bg-white/[0.02] rounded-xl border border-white/[0.04]" data-testid={`attendance-${a.date}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${statusColors[a.status] || "bg-white/5 text-muted-foreground"}`}>
                              <CalendarCheck className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-semibold text-foreground">
                              {new Date(a.date + "T00:00:00").toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] capitalize ${statusColors[a.status] || ""}`}>{a.status}</Badge>
                            <Button variant="ghost" size="icon" className="w-6 h-6 text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                try {
                                  await attendanceAPI.remove(clientId, a.date);
                                  setAttendance(attendance.filter(x => x.date !== a.date));
                                  toast.success("Attendance removed");
                                } catch { toast.error("Failed to delete"); }
                              }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {showProgressCard && (
        <ProgressCard 
          client={client} 
          progress={progress}
          coachName={trainer?.name}
          onClose={() => setShowProgressCard(false)} 
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, type: null, id: null, name: null })}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              Are you sure you want to delete this {deleteConfirm.name}?
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ show: false, type: null, id: null, name: null })}
              className="border-border"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Change Dialog */}
      <Dialog open={showGoalChange} onOpenChange={setShowGoalChange}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-tight">Change Goal</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Previous progress history will be saved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 rounded-xl bg-[#00BFFF]/5 border border-[#00BFFF]/20">
              <p className="text-xs text-[#00BFFF]">Current goal: <span className="font-medium capitalize">{client?.goal?.replace(/_/g, ' ')}</span></p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">New Goal</Label>
              <Select value={newGoal} onValueChange={setNewGoal}>
                <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="goal-change-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fat_loss">Fat Loss</SelectItem>
                  <SelectItem value="muscle_gain">Muscle Gain</SelectItem>
                  <SelectItem value="recomp">Recomposition</SelectItem>
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="general_fitness">General Fitness</SelectItem>
                  <SelectItem value="competition_prep">Competition Prep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04] cursor-pointer">
              <input type="checkbox" checked={createNewPlanOnGoalChange} onChange={(e) => setCreateNewPlanOnGoalChange(e.target.checked)} className="accent-[#00BFFF] w-4 h-4" data-testid="create-new-plan-checkbox" />
              <span className="text-xs text-muted-foreground">Start new plan (keep previous plan history)</span>
            </label>
            <Button onClick={handleGoalChange} disabled={!newGoal || newGoal === client?.goal} className="w-full h-11 btn-premium text-white font-semibold text-xs uppercase rounded-xl" data-testid="confirm-goal-change-btn">
              Update Goal
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-tight">Edit Client</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Update client information.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name</Label>
                <Input value={editData.name || ""} onChange={(e) => setEditData({...editData, name: e.target.value})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Age</Label>
                <Input type="number" value={editData.age || ""} onChange={(e) => setEditData({...editData, age: parseInt(e.target.value) || 0})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-age" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Gender</Label>
                <Select value={editData.gender || "male"} onValueChange={(v) => setEditData({...editData, gender: v})}>
                  <SelectTrigger className="h-11 bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Height (cm)</Label>
                <Input type="number" step="0.1" value={editData.height || ""} onChange={(e) => setEditData({...editData, height: parseFloat(e.target.value) || null})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-height" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Current Wt</Label>
                <Input type="number" step="0.1" value={editData.current_weight || ""} onChange={(e) => setEditData({...editData, current_weight: parseFloat(e.target.value) || null})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-weight" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Phone</Label>
                <Input value={editData.phone || ""} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-phone" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email</Label>
                <Input value={editData.email || ""} onChange={(e) => setEditData({...editData, email: e.target.value})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Target Weight</Label>
                <Input type="number" step="0.1" value={editData.target_weight || ""} onChange={(e) => setEditData({...editData, target_weight: parseFloat(e.target.value) || null})} className="h-11 bg-secondary/50 border-transparent" data-testid="edit-client-target-weight" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Priority</Label>
                <Select value={editData.priority || "normal"} onValueChange={(v) => setEditData({...editData, priority: v})}>
                  <SelectTrigger className="h-11 bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="priority">Priority</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Client Type</Label>
                <Select value={editData.client_type || ""} onValueChange={(v) => setEditData({...editData, client_type: v})}>
                  <SelectTrigger className="h-11 bg-secondary/50 border-transparent"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online Coaching</SelectItem>
                    <SelectItem value="offline">Offline Training</SelectItem>
                    <SelectItem value="personal">Personal Training</SelectItem>
                    <SelectItem value="diet_only">Diet Only</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Activity Level</Label>
                <Select value={editData.activity_level || ""} onValueChange={(v) => setEditData({...editData, activity_level: v})}>
                  <SelectTrigger className="h-11 bg-secondary/50 border-transparent"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">Sedentary</SelectItem>
                    <SelectItem value="light_active">Light Active</SelectItem>
                    <SelectItem value="moderately_active">Moderately Active</SelectItem>
                    <SelectItem value="very_active">Very Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Medical Conditions</Label>
              <Textarea value={editData.medical_conditions || ""} onChange={(e) => setEditData({...editData, medical_conditions: e.target.value})} className="bg-secondary/50 border-transparent min-h-[50px] text-sm" data-testid="edit-client-medical" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Injuries</Label>
              <Textarea value={editData.injuries || ""} onChange={(e) => setEditData({...editData, injuries: e.target.value})} className="bg-secondary/50 border-transparent min-h-[50px] text-sm" data-testid="edit-client-injuries" />
            </div>
            {/* Baseline Measurements */}
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Baseline Measurements (cm)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.1" value={editData.baseline_chest || ""} onChange={(e) => setEditData({...editData, baseline_chest: e.target.value})} placeholder="Chest" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-chest" />
                <Input type="number" step="0.1" value={editData.baseline_waist || ""} onChange={(e) => setEditData({...editData, baseline_waist: e.target.value})} placeholder="Waist" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-waist" />
                <Input type="number" step="0.1" value={editData.baseline_hips || ""} onChange={(e) => setEditData({...editData, baseline_hips: e.target.value})} placeholder="Hips" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-hips" />
                <Input type="number" step="0.1" value={editData.baseline_arms || ""} onChange={(e) => setEditData({...editData, baseline_arms: e.target.value})} placeholder="Arms" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-arms" />
                <Input type="number" step="0.1" value={editData.baseline_thighs || ""} onChange={(e) => setEditData({...editData, baseline_thighs: e.target.value})} placeholder="Thighs" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-thighs" />
                <Input type="number" step="0.1" value={editData.baseline_calf || ""} onChange={(e) => setEditData({...editData, baseline_calf: e.target.value})} placeholder="Calf" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-calf" />
                <Input type="number" step="0.1" value={editData.baseline_neck || ""} onChange={(e) => setEditData({...editData, baseline_neck: e.target.value})} placeholder="Neck" className="h-10 bg-secondary/50 border-transparent text-sm" data-testid="edit-baseline-neck" />
              </div>
            </div>
            <Button onClick={handleEditClient} className="w-full h-11 btn-premium text-white font-semibold text-xs uppercase rounded-xl" data-testid="save-edit-client-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => { setShowCropper(false); setCropImage(null); }}
        imageSrc={cropImage}
        onCropComplete={handleCroppedPhotoUpload}
        aspectRatio={1}
      />
    </div>
  );
}
